import { pickLocale } from "./format";

/**
 * schema.org nodes for a product listing.
 *
 * Category and collection pages emitted no structured data at all — no
 * `CollectionPage`, no `ItemList`, no `BreadcrumbList` — while the product page
 * now carries `Product` and `Offer`. A crawler could read an individual piece
 * and understood nothing about the shelves it sits on.
 *
 * Built here rather than behind a new endpoint because, unlike `Product`, a
 * listing node needs nothing the page has not already fetched: the title, the
 * description and the visible products are all in hand. Adding a request to
 * restate them would cost a round trip and could disagree with what is on
 * screen. (`Product` stays server-side because its `availability` has to come
 * from live inventory, which the listing payload does not carry.)
 */
const SITE = "Atelier Di Falco";

function absolute(path) {
  if (typeof window === "undefined") return path;
  return new URL(path, window.location.origin).toString();
}

/**
 * @param kind        "category" | "collection"
 * @param handle      the listing's handle
 * @param title       display title, string or a locale dict
 * @param description display description, string or a locale dict
 * @param products    the products currently rendered
 * @param locale      active locale
 * @param label       breadcrumb label for the parent section
 * @param basePath    section root, when the page is not under /categories or
 *                    /collections. A standalone shelf like /outlet has no
 *                    parent segment, so it passes its own path and `handle` is
 *                    left empty — emitting /collections/outlet would put a URL
 *                    that 404s into the structured data, which is worse than
 *                    emitting none.
 */
export function buildListingJsonLd({ kind, handle, title, description, products = [], locale = "az", label, basePath }) {
  const base = basePath || (kind === "category" ? "/categories" : "/collections");
  const url = absolute(handle ? `${base}/${handle}` : base);
  const name = pickLocale(title, locale) || (typeof title === "string" ? title : handle);
  const text = pickLocale(description, locale) || (typeof description === "string" ? description : "");

  const items = products
    .map((product, index) => {
      const productHandle = product?.handle;
      if (!productHandle) return null;
      return {
        "@type": "ListItem",
        position: index + 1,
        url: absolute(`/products/${productHandle}`),
        name: pickLocale(product.title, locale) || productHandle,
      };
    })
    .filter(Boolean);

  const collectionPage = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    url,
    ...(text ? { description: text } : {}),
    isPartOf: { "@type": "WebSite", name: SITE, url: absolute("/") },
    // Only the products actually on this page. Claiming the full result count
    // here while listing 48 of them is the kind of mismatch that gets
    // structured data ignored.
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: items.length,
      itemListElement: items,
    },
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: locale === "az" ? "Ana səhifə" : "Home", item: absolute("/") },
      { "@type": "ListItem", position: 2, name: label || (locale === "az" ? "Kataloq" : "Shop"), item: absolute(base) },
      { "@type": "ListItem", position: 3, name, item: url },
    ],
  };

  return [collectionPage, breadcrumb];
}

export default buildListingJsonLd;
